/**
 * @file PlanOverviewSection.jsx
 * @module ui/src/components/tenant-admin/sections/PlanOverviewSection
 * @description
 *   Provides the document-level orientation surface for a compensation plan,
 *   combining server-generated economic narrative, temporal applicability,
 *   design provenance, Layer 0 state, modifier summary, population freshness,
 *   and the CRL recommendation without becoming an edit surface.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit LLC · DecisionFlow · S775
 *
 * @architecture
 *   Locked decisions : #1, #13, #17
 *   Temporal invariants : INV-3
 *   Design principles : DP-1, DP-5, DP-12, DP-14
 *   Cross-cutting contracts : XC-1, XC-7
 *
 * @domain-rules
 *   - Plan Overview is read-oriented and does not directly mutate plan configuration.
 *   - Economic narrative is rendered from the server-generated PlanSummaryPayload through C-62.
 *   - planStatus is a workflow label only and never determines temporal applicability.
 *   - Effective applicability comes only from effectiveFrom/effectiveTo.
 *   - Missing effective dates are surfaced explicitly and never inferred from fiscal period.
 *   - primarySpineLabel is human-readable; internal spine keys are never rendered.
 *   - Layer 2 modifiers are presented in DP-7 canonical order.
 *   - Settlement and Non-Cash are excluded because each has a dedicated plan-document section.
 *   - Population freshness is rendered through C-46 rather than inferred locally.
 *   - CRL divergence and acknowledgement behavior is delegated to C-20.
 *
 * @session-6-migration-flags
 *   - effective_from on plan — pending m_tc_01
 *   - effective_to on plan — pending m_tc_01; nullable/open-ended
 *   - primary_spine on plan_component — pending Session 6 domain migration
 *   - plan_layer0_config — new Session 6 configuration table
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group C batch delivery
 */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import CRLRecommendationCard from '../CRLRecommendationCard';
import DataFreshnessIndicator from '../../shared/DataFreshnessIndicator';
import PlanNarrativeSummary from '../../shared/PlanNarrativeSummary';

/**
 * @typedef {Object} ModifierSummary
 * @property {string} type
 * @property {boolean} enabled
 * @property {string} [summary]
 */

/**
 * @typedef {Object} PopulationSummary
 * @property {number} count
 * @property {string} freshnessAt
 * @property {'current'|'stale'|'warning'} status
 */

/**
 * @typedef {Object} PlanOverviewPayload
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {string} planStatus
 * @property {number} version
 * @property {string} effectiveFrom
 * @property {string|null} effectiveTo
 * @property {'crl'|'crl_override'|'custom'} designSource
 * @property {string} primarySpineLabel
 * @property {boolean} layer0Active
 * @property {string} layer0Summary
 * @property {ModifierSummary[]} modifiers
 */

/**
 * @typedef {Object} CRLState
 * @property {Object} recommendation
 * @property {Object} currentConfig
 */

/**
 * @typedef {Object} PlanOverviewSectionProps
 * @property {PlanOverviewPayload} plan
 * @property {Object} narrative
 * @property {PopulationSummary} population
 * @property {CRLState} crlState
 * @property {boolean} readOnly
 * @property {Function} onCRLApply
 * @property {Function} onCRLDismiss
 * @property {Function} onNavigate
 */

// BUG-11 FIX: MODIFIER_ORDER must use raw modifierType keys (from D8 manifest / buildModifierSummary).
// Was using display labels ('Revenue Type', 'Kicker', etc.) which never matched raw keys —
// indexOf() always returned -1, ordering was always MAX_SAFE_INTEGER for every modifier.
// contest and non_cash added (present in S812 PCMA matrix, may appear in layer2Config).
const MODIFIER_ORDER = Object.freeze([
  'rev_type_differentials',
  'multi_year_deal',
  'spiff',
  'contest',
  'kicker',
  'draw_guarantee',
  'cap',
  'clawback',
  'non_cash',
]);

// Human-readable labels for raw modifier type keys — used in the overview list display.
const MODIFIER_TYPE_LABELS = Object.freeze({
  rev_type_differentials: 'Revenue Type Differentials',
  multi_year_deal:        'Multi-Year Deal',
  spiff:                  'SPIFF / Tactical',
  contest:                'Contest',
  kicker:                 'Kicker / Multiplier',
  draw_guarantee:         'Draw / Guarantee',
  cap:                    'Period Cap',
  clawback:               'Churn Clawback',
  non_cash:               'Non-Cash Awards',
});

// BUG-07: creditingMode display labels for the credit allocation card.
// plan.layer0Summary is always empty — EP2 layer0Config has no 'summary' field.
// Use creditingMode from _layer0Raw directly with human-readable labels.
const CREDITING_MODE_LABELS = Object.freeze({
  individual:  'Individual · 100% direct credit',
  deal_split:  'Deal Split · credit shared across contributors',
  territory:   'Territory · credit by geographic assignment',
  overlay:     'Overlay · specialist overlay credit model',
  channel:     'Channel / Partner · partner-attributed credit',
});

// non_cash is excluded — it has a dedicated section. settlement is not a layer2Config
// modifier type (it lives in settlementConfig), so it won't appear here in practice.
const EXCLUDED_OVERVIEW_MODIFIERS = new Set(['non_cash']);

const WORKFLOW_LABELS = Object.freeze({
  draft: 'Draft',
  pending_approval: 'Pending Approval',
  approved: 'Approved',
  superseded: 'Superseded',
  withdrawn: 'Withdrawn',
});

const DESIGN_SOURCE_LABELS = Object.freeze({
  crl: 'CRL recommendation',
  crl_override: 'CRL recommendation with acknowledged departure',
  custom: 'Custom design',
});

const styles = {
  root: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    color: 'rgb(var(--color-text))',
  },
  card: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 8,
    background: 'rgb(var(--color-surface))',
    padding: 16,
    marginBottom: 14,
  },
  headingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: 999,
    padding: '2px 8px',
    fontSize: 12,
    fontWeight: 600,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 14,
  },
  label: {
    display: 'block',
    color: 'rgb(var(--color-text-muted))',
    fontSize: 12,
    marginBottom: 3,
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  listRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    padding: '10px 0',
    borderBottom: '1px solid rgb(var(--color-border))',
  },
};

/**
 * WHY: The server provides effective applicability. Formatting it is safe;
 * deriving it from planStatus or fiscal period would violate INV-3 and DP-1.
 */
function formatEffectiveWindow(effectiveFrom, effectiveTo) {
  if (!effectiveFrom) {
    return 'Effective period: not yet recorded';
  }

  const start = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${effectiveFrom}T00:00:00`));

  if (!effectiveTo) {
    return `${start} → Open-ended`;
  }

  const end = new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${effectiveTo}T00:00:00`));

  return `${start} → ${end}`;
}

/**
 * @param {PlanOverviewSectionProps} props
 * @returns {JSX.Element}
 */
export default function PlanOverviewSection({
  plan,
  narrative,
  population,
  crlState,
  readOnly,
  onCRLApply,
  onCRLDismiss,
  onNavigate,
  onViewPopulation,
}) {
  // WHY: The overview keeps the CRL evidence present but allows the administrator
  // to progressively disclose the more detailed recommendation card (DP-5).
  const [crlExpanded, setCrlExpanded] = useState(false);

  // WHY: Modifier ordering is a document presentation contract (#17 / DP-7),
  // not an economic inference. We use only already human-readable server summary
  // items and never parse raw configuration values (DP-12 / XC-1).
  const orderedModifiers = useMemo(() => {
    const visible = plan.modifiers.filter(
      (modifier) => !EXCLUDED_OVERVIEW_MODIFIERS.has(modifier.type),
    );

    return [...visible].sort((left, right) => {
      const leftIndex = MODIFIER_ORDER.indexOf(left.type);
      const rightIndex = MODIFIER_ORDER.indexOf(right.type);

      const normalizedLeft =
        leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
      const normalizedRight =
        rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

      return normalizedLeft - normalizedRight;
    });
  }, [plan.modifiers]);

  return (
    <section
      style={styles.root}
      aria-labelledby={`plan-overview-heading-${plan.planId}`}
    >
      <div style={styles.headingRow}>
        <div>
          <h2 id={`plan-overview-heading-${plan.planId}`} style={{ marginTop: 0 }}>
            Plan overview
          </h2>
          <p style={{ ...styles.muted, marginTop: 0 }}>
            Document-level summary for {plan.planName} · V{plan.version}
          </p>
        </div>

        {/* WHY: Workflow status is deliberately secondary and textual. It is not
            consulted anywhere to determine effective applicability or visibility. */}
        <span style={styles.badge}>
          Workflow: {WORKFLOW_LABELS[plan.planStatus] || plan.planStatus}
        </span>
      </div>

      <div style={styles.card}>
        {/*
         * WHY: C-62 owns deterministic economic narrative generation/rendering.
         * C-50 never reads plan config to manufacture an economic explanation
         * (DP-12 / XC-1).
         */}
        <PlanNarrativeSummary
          summary={narrative}
          persona="ca1"
        />
      </div>

      <div style={styles.card}>
        <h3 style={{ marginTop: 0 }}>Applicability & design</h3>

        <div style={styles.grid}>
          <div>
            <span style={styles.label}>Effective applicability</span>
            <strong>
              {formatEffectiveWindow(plan.effectiveFrom, plan.effectiveTo)}
            </strong>
          </div>

          <div>
            <span style={styles.label}>Design source</span>
            <strong>
              {DESIGN_SOURCE_LABELS[plan.designSource] || plan.designSource}
            </strong>
          </div>

          <div>
            <span style={styles.label}>Primary earning model</span>
            <strong>{plan.primarySpineLabel || 'Not configured'}</strong>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.headingRow}>
          <div>
            <h3 style={{ margin: 0 }}>Credit allocation</h3>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              Layer 0 defines how credit is allocated before Layer 1 measurement
              and economics are evaluated.
            </p>
          </div>

          {/*
           * WHY: Overview remains read-oriented. Navigation delegates editing to
           * the dedicated Layer 0 section rather than adding duplicate controls.
           */}
          <button
            type="button"
            className="text-button"
            onClick={() => onNavigate('credit-allocation')}
          >
            Edit in Credit Allocation section
          </button>
        </div>

        {/* BUG-07 FIX: layer0Summary is always '' (EP2 layer0Config has no 'summary' field).
            Use creditingMode from _layer0Raw with human-readable label. */}
        <p style={{ marginBottom: 0 }}>
          {(() => {
            const mode = plan._layer0Raw?.creditingMode ?? plan._layer0Raw?.crediting_mode;
            if (!mode || mode === 'individual') {
              return 'Credit allocation: Individual · 100% credit — configure non-individual modes in the Crediting section.';
            }
            return `Credit allocation: ${CREDITING_MODE_LABELS[mode] ?? mode}`;
          })()}
        </p>
      </div>

      <div style={styles.card}>
        <div style={styles.headingRow}>
          <div>
            <h3 style={{ margin: 0 }}>Measurement & adjustments</h3>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              Summary only. Configure detailed economics in their dedicated
              document sections.
            </p>
          </div>

          <button
            type="button"
            className="text-button"
            onClick={() => onNavigate('measurement-economics')}
          >
            Edit in Measurement &amp; Economics section
          </button>
        </div>

        <div style={{ marginTop: 14 }}>
          <span style={styles.label}>Primary earning model</span>
          <strong>{plan.primarySpineLabel || 'Not configured'}</strong>
        </div>

        <div style={{ marginTop: 16 }}>
          <span style={styles.label}>Configured Layer 2 modifiers</span>

          {orderedModifiers.length === 0 ? (
            <p style={styles.muted}>No Layer 2 modifiers are configured.</p>
          ) : (
            <ul style={styles.list}>
              {orderedModifiers.map((modifier) => (
                <li
                  key={`${modifier.type}-${modifier.summary || ''}`}
                  style={styles.listRow}
                >
                  <div>
                    <strong>{MODIFIER_TYPE_LABELS[modifier.type] ?? modifier.type}</strong>
                    {modifier.enabled && modifier.summary && (
                      <div style={styles.muted}>{modifier.summary}</div>
                    )}
                  </div>

                  <span style={styles.badge}>
                    {modifier.enabled ? 'Enabled' : 'Not enabled'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.headingRow}>
          <div>
            <h3 style={{ margin: 0 }}>Matching population</h3>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              {population.count.toLocaleString()} matched position
              {population.count === 1 ? '' : 's'}
            </p>
          </div>

          {/*
           * WHY: Population freshness has domain semantics beyond a simple
           * boolean. C-46 owns the signal-specific presentation rather than C-50
           * reducing freshness to a locally inferred current/not-current flag.
           */}
          <DataFreshnessIndicator
            signal="population"
            status={population.status}
            freshnessAt={population.freshnessAt}
          />
        </div>

        {/* D3: assignment-scope removed from plan authoring flow. Population is managed
            in the Assignments tab. Population drawer provides read-only view of resolved members.
            S816: onViewPopulation triggers MatchingPopulationDrawer (OI-S815-MATCHINGPOPULATION-TRIGGER). */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <p style={{ ...styles.muted, fontSize: '0.85rem', margin: 0 }}>
            Positions are assigned from the Assignments tab. Population freshness reflects last resolution.
          </p>
          <button
            type="button"
            className="text-button"
            onClick={onViewPopulation}
          >
            View matching population
          </button>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.headingRow}>
          <div>
            <h3 style={{ margin: 0 }}>Comp Role Library recommendation</h3>
            <p style={{ ...styles.muted, marginBottom: 0 }}>
              Review the role-based design recommendation and any acknowledged
              departure.
            </p>
          </div>

          <button
            type="button"
            className="text-button"
            aria-expanded={crlExpanded}
            aria-controls={`crl-overview-${plan.planId}`}
            onClick={() => setCrlExpanded((expanded) => !expanded)}
          >
            {crlExpanded ? 'Hide CRL recommendation' : 'Show CRL recommendation'}
          </button>
        </div>

        {crlExpanded && (
          <div id={`crl-overview-${plan.planId}`} style={{ marginTop: 14 }}>
            {/*
             * WHY: C-20 owns CRL divergence acknowledgement. The overview only
             * forwards the server recommendation/current-config contract and
             * parent actions; it does not independently infer divergence.
             */}
            <CRLRecommendationCard
              recommendation={crlState.recommendation}
              currentConfig={crlState.currentConfig}
              readOnly={readOnly}
              onApply={onCRLApply}
              onDismiss={onCRLDismiss}
            />
          </div>
        )}
      </div>
    </section>
  );
}

PlanOverviewSection.propTypes = {
  plan: PropTypes.shape({
    planId: PropTypes.number.isRequired,
    planCode: PropTypes.string.isRequired,
    planName: PropTypes.string.isRequired,
    planStatus: PropTypes.string.isRequired,
    version: PropTypes.number.isRequired,
    effectiveFrom: PropTypes.string,
    effectiveTo: PropTypes.string,
    designSource: PropTypes.oneOf(['crl', 'crl_override', 'custom']).isRequired,
    primarySpineLabel: PropTypes.string.isRequired,
    layer0Active: PropTypes.bool.isRequired,
    layer0Summary: PropTypes.string.isRequired,
    modifiers: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        enabled: PropTypes.bool.isRequired,
        summary: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
  narrative: PropTypes.object.isRequired,
  population: PropTypes.shape({
    count: PropTypes.number.isRequired,
    freshnessAt: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['current', 'stale', 'warning']).isRequired,
  }).isRequired,
  crlState: PropTypes.shape({
    recommendation: PropTypes.object.isRequired,
    currentConfig: PropTypes.object.isRequired,
  }).isRequired,
  readOnly: PropTypes.bool.isRequired,
  onCRLApply: PropTypes.func.isRequired,
  onCRLDismiss: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  onViewPopulation: PropTypes.func.isRequired,
};

/**
 * @example
 * <PlanOverviewSection
 *   plan={planOverview}
 *   narrative={planNarrative}
 *   population={{
 *     count: 842,
 *     freshnessAt: '2026-08-12T12:20:00Z',
 *     status: 'current',
 *   }}
 *   crlState={{
 *     recommendation: crlRecommendation,
 *     currentConfig: authoredPlanConfig,
 *   }}
 *   readOnly={false}
 *   onCRLApply={restoreCRL}
 *   onCRLDismiss={acknowledgeCRLDeparture}
 *   onNavigate={(sectionKey) => navigateToPlanSection(sectionKey)}
 * />
 */

/*
 * DOMAIN COMPLIANCE CHECK
 * - Locked decision #1: this is an integrated summary of one atomic plan document.
 * - Locked decision #13: CRL recommendation and departure are delegated to C-20.
 * - Locked decision #17 / DP-7: modifier summaries are ordered Revenue Type →
 *   Multi-Year → SPIFF/Contest → Kicker → Draw/Guarantee → Cap → Clawback.
 * - Settlement and Non-Cash are explicitly excluded from overview modifier rendering.
 * - INV-3 / DP-1: planStatus is rendered only as workflow metadata; effective dates are separate.
 * - DP-12 / XC-1: economic narrative comes exclusively through C-62.
 * - C-46 renders population freshness; C-50 does not infer freshness semantics.
 * - primarySpineLabel is displayed; no internal spine code exists in this component contract.
 * - The locked Layer 0 inactive microcopy is rendered verbatim.
 * - No edit fields exist; buttons navigate to dedicated configuration sections.
 */

/*
 * SESSION 6 MIGRATION FLAGS
 * - plan.effective_from — pending m_tc_01
 * - plan.effective_to — pending m_tc_01; nullable/open-ended
 * - plan_component.primary_spine — domain addition
 * - plan_layer0_config — new Session 6 configuration table
 */

/*
 * ACCESSIBILITY NOTES
 * - The section has an explicit heading association.
 * - Workflow and modifier states use visible text labels.
 * - CRL progressive disclosure uses aria-expanded and aria-controls.
 * - Navigation controls explicitly name their destination section.
 * - Population freshness accessibility remains delegated to C-46.
 */